import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import webSocketManager from '../services/WebSocketManager';
import { CONNECTION_STATES } from '../constants/webSocketConstants';

/**
 * WebSocket Context for managing global WebSocket connection
 * 
 * This provider:
 * - Manages the global WebSocket connection
 * - Handles authentication integration
 * - Provides connection state to all components
 * - Manages subscription lifecycle
 * - Handles reconnection on auth token refresh
 */

// Create Context
const WebSocketContext = createContext(null);

// Custom hook to use WebSocket context
export const useWebSocketContext = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketContext must be used within a WebSocketProvider');
  }
  return context;
};

/**
 * WebSocket Provider Component
 * 
 * @param {Object} props
 * @param {ReactNode} props.children - Child components
 * @param {Object} props.user - Current user object
 * @param {boolean} props.enabled - Whether WebSocket should be enabled (default: true)
 */
export const WebSocketProvider = ({ 
  children, 
  user, 
  enabled = true 
}) => {
  // Connection state
  const [connectionState, setConnectionState] = useState(CONNECTION_STATES.DISCONNECTED);
  const [lastConnectedAt, setLastConnectedAt] = useState(null);
  const [lastError, setLastError] = useState(null);
  
  // Refs for stable references
  const userRef = useRef(user);
  const enabledRef = useRef(enabled);
  const connectionCleanupRef = useRef(null);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  // Connection state listener
  useEffect(() => {
    const cleanup = webSocketManager.addConnectionListener((state) => {
      setConnectionState(state);
      
      if (state === CONNECTION_STATES.CONNECTED) {
        setLastConnectedAt(new Date());
        setLastError(null);
      } else if (state === CONNECTION_STATES.ERROR) {
        setLastError(new Error('WebSocket connection failed'));
      }
    });

    connectionCleanupRef.current = cleanup;
    return cleanup;
  }, []);

  /**
   * Get cookie value by name
   */
  const getCookie = (name) => {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? match[2] : null;
  };

  /**
   * Connect to WebSocket server
   */
  const connectWebSocket = useCallback(async () => {
    if (!userRef.current) {
      console.error('⚠️ Cannot connect WebSocket: Missing user');
      return;
    }

    try {
      // In development, pass token as query param since cookies don't work cross-port
      // In production, ALB path-based routing makes cookies work automatically
      const token = process.env.NODE_ENV === 'development' ? getCookie('access_token') : null;
      
      if (process.env.NODE_ENV === 'development' && !token) {
        console.error('⚠️ No access_token cookie found in development mode!');
      }
      
      await webSocketManager.connect(token, {
        userId: userRef.current.id,
        username: userRef.current.username
      });
    } catch (error) {
      console.error('❌ Failed to connect WebSocket:', error);
      setLastError(error);
    }
  }, []);

  /**
   * Disconnect from WebSocket server
   */
  const disconnectWebSocket = useCallback(() => {
    webSocketManager.disconnect();
    setLastConnectedAt(null);
    setLastError(null);
  }, []);

  /**
   * Reconnect WebSocket (disconnect and connect again)
   */
  const reconnectWebSocket = useCallback(async () => {
    webSocketManager.disconnect();
    
    // Wait a bit before reconnecting
    setTimeout(() => {
      connectWebSocket();
    }, 1000);
  }, [connectWebSocket]);

  // Connect/disconnect based on enabled state and user
  useEffect(() => {
    const shouldConnect = enabled && user;
    const isCurrentlyConnected = webSocketManager.isConnected();

    if (shouldConnect && !isCurrentlyConnected) {
      // Connect
      connectWebSocket();
    } else if (!shouldConnect && isCurrentlyConnected) {
      // Disconnect
      disconnectWebSocket();
    }
  }, [enabled, user, connectWebSocket, disconnectWebSocket]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (connectionCleanupRef.current) {
        connectionCleanupRef.current();
      }
      webSocketManager.disconnect();
    };
  }, []);

  /**
   * Subscribe to a WebSocket destination
   * @param {string} destination - Destination to subscribe to
   * @param {function} messageHandler - Message handler callback
   * @param {Object} options - Subscription options
   * @returns {Object} subscription object with unsubscribe method
   */
  const subscribe = useCallback((destination, messageHandler, options = {}) => {
    const managerConnected = webSocketManager.isConnected();
    
    if (!managerConnected) {
      console.warn(`⚠️ Cannot subscribe to ${destination}: WebSocket not connected`);
      return { unsubscribe: () => {} }; // Return no-op
    }

    const subscriptionId = webSocketManager.subscribe(
      destination, 
      messageHandler, 
      options.headers
    );

    if (!subscriptionId) {
      console.error(`❌ Failed to subscribe to ${destination}`);
      return { unsubscribe: () => {} };
    }

    return {
      subscriptionId,
      destination,
      unsubscribe: () => {
        webSocketManager.unsubscribe(subscriptionId);
      }
    };
  }, [connectionState]);

  /**
   * Get current connection status information
   */
  const getConnectionInfo = useCallback(() => {
    return {
      state: connectionState,
      isConnected: connectionState === CONNECTION_STATES.CONNECTED,
      lastConnectedAt,
      lastError,
      user: userRef.current,
      enabled: enabledRef.current
    };
  }, [connectionState, lastConnectedAt, lastError]);

  // Reactive isConnected value based on connection state
  const isConnected = connectionState === CONNECTION_STATES.CONNECTED;

  // Context value
  const contextValue = {
    // Connection state
    connectionState,
    isConnected,
    lastConnectedAt,
    lastError,
    
    // Connection methods
    connect: connectWebSocket,
    disconnect: disconnectWebSocket,
    reconnect: reconnectWebSocket,
    
    // Messaging methods
    subscribe,
    send: webSocketManager.send.bind(webSocketManager),
    
    // Utility methods
    getConnectionInfo,
    
    // Configuration
    enabled,
    user
  };

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketProvider;
